import { useRef, useState, useCallback, useEffect } from 'react';
import { compose } from 'redux';
import withStyles from '@material-ui/styles/withStyles';

import moment from 'moment';
import Highcharts from 'highcharts';
import gantt from 'highcharts/highcharts-gantt';
import HighchartsReact from 'highcharts-react-official';

import { Grid, Typography } from '@material-ui/core';
import highchartsTheme from 'layout/highcharts.theme';
import winchHighchartsTheme from 'features/projects/dashboard/components/dashboards/winch/winch.highcharts.theme';
import themes, { defaultThemeType } from 'layout/themes';
import {
  ACTIVITY_LOG_STATUSES,
  ACTIVITY_TYPES,
  Filters,
} from 'features/wells/overview/activityLog/helpers/activityLog.constants';
import { getRichEditorText } from 'utils/app.util';
import { isMomentObject } from 'features/wells/overview/activityLog/helpers/activityLog.validators';

const theme = themes[defaultThemeType];

const { fontColor, defaultFontSize } = winchHighchartsTheme.style;

const ActivityLogsGanttChart = ({
  classes,
  activityLogs,
  filter,
  chartBackgroundColor = theme.altus.background.dashboard,
  showDefaultButtons = true,
  showNavigatorChart = true,
}) => {
  const chartComponent = useRef(null);

  useEffect(() => {
    if (chartComponent?.current) {
      const chart = chartComponent?.current?.chart;
      if (chart?.xAxis) {
        chart.xAxis[0]?.setExtremes(
          isMomentObject(filter[Filters.FROM])
            ? filter[Filters.FROM].valueOf()
            : moment(filter[Filters.FROM]).valueOf(),
          isMomentObject(filter[Filters.TO])
            ? filter[Filters.TO].valueOf()
            : moment(filter[Filters.TO]).valueOf(),
          true,
          true,
        );
      }
    }
  }, [filter, activityLogs]);

  useEffect(() => {
    const chart = chartComponent.current.chart;
    const updateTodayLine = () => {
      const now = Date.now();
      chart.xAxis[0].removePlotLine('today-line');
      chart.xAxis[0].addPlotLine({
        id: 'today-line',
        value: now,
        color: theme.palette.error.main,
        width: 2,
        zIndex: 5,
        label: {
          text: 'Today',
          rotation: 0,
          align: 'center',
          x: -5,
          y: -100,
          style: {
            color: theme.palette.error.main,
            whiteSpace: 'nowrap',
          },
        },
      });
    };

    updateTodayLine();
    const timer = setInterval(updateTodayLine, 60 * 60 * 1000); // every hour

    return () => clearInterval(timer);
  }, []);

  const updateSeriesData = useCallback((activityLogs) => {
    const chart = chartComponent.current.chart;

    if (chart?.series) {
      for (let i = chart.series.length - 1; i >= 0; i--) {
        chart.series[i].remove(false);
      }
    }

    const seriesToAdd = activityLogs?.map((log) => {
      const activityStart = log.startDate
        ? moment(log.startDate).toDate().getTime()
        : null;
      const activityEnd = log.endDate
        ? moment(log.endDate).toDate().getTime()
        : null;

      const activityToAdd = {
        name: log.activityName,
        activityType: ACTIVITY_TYPES.find(
          (type) => type.id === log.activityType,
        ).name,
        id: log.activityLogId,
        description: getRichEditorText(log.description),
        start: activityStart,
        end: activityEnd,
        status: ACTIVITY_LOG_STATUSES.find((type) => type.id === log.status)
          .name,
        responsible: log.responsibleUser?.fullName,
        comment: getRichEditorText(log.comment),
        mitigationSolutions: getRichEditorText(log.mitigationSolutions),
        showCheckbox: false,
      };

      const seriesToAdd = {
        id: log.activityLogId,
        name: log.activityName,
        data: [activityToAdd],
      };

      return seriesToAdd;
    });

    seriesToAdd?.toJS().forEach((serie) => {
      const isSeriesAlreadyIncluded = chart.series
        .map((x) => x.userOptions.id)
        .includes(serie.id);
      if (!isSeriesAlreadyIncluded) {
        chart.addSeries(serie, false);
      }
    });

    seriesToAdd?.toJS().forEach((newSeries) => {
      const existingSeries = chart.series.find(
        (s) => s.userOptions.id === newSeries.id,
      );
      if (existingSeries) {
        existingSeries.update(newSeries, false);
      } else {
        chart.addSeries(newSeries, false);
      }
    });

    chart.redraw();
    chart.reflow();
  }, []);

  useEffect(() => {
    updateSeriesData(activityLogs);
  }, [activityLogs, updateSeriesData]);

  const [chartOptions] = useState(
    Highcharts.merge(false, highchartsTheme, {
      chart: {
        backgroundColor: chartBackgroundColor,
      },
      rangeSelector: {
        inputEnabled: false,
        enabled: showDefaultButtons,
        selected: 6,
        labels: {
          style: {
            color: fontColor,
            fontSize: defaultFontSize,
          },
        },
        buttons: showDefaultButtons
          ? [
              {
                type: 'minute',
                count: 1,
                text: '1mn',
                title: 'View 1 minute',
              },
              {
                type: 'month',
                count: 1,
                text: '1m',
                title: 'View 1 month',
                events: {
                  click: function () {
                    const chart = chartComponent.current.chart;
                    const now = moment().valueOf();
                    const halfMonthMs =
                      moment.duration(1, 'months').asMilliseconds() / 2;
                    chart.xAxis[0].setExtremes(
                      now - halfMonthMs,
                      now + halfMonthMs,
                      true,
                      true,
                    );
                    return false;
                  },
                },
              },
              {
                type: 'month',
                count: 3,
                text: '3m',
                title: 'View 3 months',
                events: {
                  click: function () {
                    const chart = chartComponent.current.chart;
                    const now = moment().valueOf();
                    const halfMonthMs =
                      moment.duration(3, 'months').asMilliseconds() / 2;
                    chart.xAxis[0].setExtremes(
                      now - halfMonthMs,
                      now + halfMonthMs,
                      true,
                      true,
                    );
                    return false;
                  },
                },
              },
              {
                type: 'month',
                count: 6,
                text: '6m',
                title: 'View 6 months',
                events: {
                  click: function () {
                    const chart = chartComponent.current.chart;
                    const now = moment().valueOf();
                    const halfMonthMs =
                      moment.duration(6, 'months').asMilliseconds() / 2;
                    chart.xAxis[0].setExtremes(
                      now - halfMonthMs,
                      now + halfMonthMs,
                      true,
                      true,
                    );
                    return false;
                  },
                },
              },
              {
                type: 'ytd',
                count: 2,
                text: 'YTD',
                title: 'View year to date',
              },
              {
                type: 'year',
                count: 1,
                text: '1y',
                title: 'View 1 year',
              },
              {
                type: 'all',
                text: 'All',
                title: 'View all',
              },
            ]
          : [],
      },
      scrollbar: {
        enabled: true,
      },
      navigator: {
        labels: {
          style: {
            color: fontColor,
            fontSize: defaultFontSize,
          },
        },
        enabled: showNavigatorChart,
        liveRedraw: true,
        series: {
          type: 'gantt',
          pointPlacement: 0.5,
          pointPadding: 0.25,
          accessibility: {
            enabled: false,
          },
        },
      },
      xAxis: {
        plotBands: {
          color: fontColor,
        },
        showEmpty: false,
        tickColor: fontColor,
        labels: {
          style: {
            color: fontColor,
            fontSize: defaultFontSize,
          },
        },
      },
      yAxis: {
        labels: {
          style: {
            color: fontColor,
            fontSize: defaultFontSize,
          },
        },
      },
      accessibility: {
        keyboardNavigation: {
          seriesNavigation: {
            mode: 'serialize',
          },
        },
      },
      tooltip: {
        headerFormat: '',
        pointFormat: `
                    <b>{point.name}  ({point.activityType})</b><br/>
                    {point.description}<br/>
                    Start: {point.start:%d-%m-%Y}    End: {point.end:%d-%m-%Y}<br/>
                    Status: {point.status}<br/>
                    Responsible: {point.responsible}<br/>
                    Comment: <br/>
                    {point.comment}<br/>
                    Solutions for Mitigating Challenges:<br/>
                    {point.mitigationSolutions}
                    `,
      },
    }),
  );

  return (
    <>
      {!activityLogs?.size && (
        <Grid container>
          <Grid item xs={12} className={classes.noContent}>
            <Typography variant="body2">
              No Activity found for the applied filters.
            </Typography>
          </Grid>
        </Grid>
      )}
      <HighchartsReact
        ref={chartComponent}
        constructorType={'ganttChart'}
        highcharts={gantt}
        options={chartOptions}
      />
    </>
  );
};

const styles = (theme) => ({
  noContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
});

export default compose(withStyles(styles))(ActivityLogsGanttChart);
